import React from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCheck, LuChevronRight, LuClock, LuTreePalm, LuTriangleAlert } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { useTranslation } from '../../../i18n/LanguageContext';
import { formatTimeLabel, getJobTime, getVisitTime } from '../../../utils/calendarSorting';
import { normalizePatientId } from '../../../utils/patientAccess';
import { jobDayNumber, jobState, pendingCompletionKeys, visitDayOffset, visitDayState, visitStatusLine } from './calendarDates';
import { leaveTypeLabel } from '../leave/leaveOptions';
import { JOB_STATE_TONES, LEAVE_TONE, PROJECT_STRIPES, PROJECT_SWATCHES, SCREEN_FAIL_TONE, VISIT_STATE_TONES } from './calendarTones';

const VISIT_TYPES = ['hastane', 'home', 'telefon', 'unplanning', 'handover'];

/** Everything shown about a visit on one day, translated. */
export const describeVisit = (t, visit, date, projectName) => {
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const offset = visitDayOffset(visit, date);
  let dayLabel = label('dayLabel.first');
  if (offset > 0) dayLabel = label('dayLabel.after', { n: offset });
  else if (offset < 0) dayLabel = label('dayLabel.before', { n: offset });
  const patient = normalizePatientId(visit.patientId) || label('noPatient');
  const status = visitStatusLine(t, visit, date);
  const tooltip = [
    projectName ? `${label('tooltip.project')}: ${projectName}` : null,
    `${label('tooltip.patient')}: ${patient}`,
    visit.visitNumber ? `${label('tooltip.visit')}: ${label('visitOrdinal', { n: visit.visitNumber })}` : null,
    `${label('tooltip.day')}: ${dayLabel}`,
    `${label('tooltip.status')}: ${status}`,
  ].filter(Boolean).join('\n');
  return {
    offset,
    dayLabel,
    patient,
    number: visit.visitNumber ? `V${visit.visitNumber}` : '',
    status,
    tooltip,
    state: visitDayState(visit, date),
    pending: pendingCompletionKeys(visit),
    time: formatTimeLabel(getVisitTime(visit)),
    type: VISIT_TYPES.includes(visit.visitType) ? label(`visitTypes.${visit.visitType}`) : (visit.visitType || ''),
  };
};

/** Name of the project a job is linked to: current name, else the one saved with it. */
export const jobProjectName = (job, projectNameOf) => (job.projectId ? projectNameOf(job.projectId) || job.projectName || '' : '');

const toneOf = (visit, state) => (visit.screenFail ? SCREEN_FAIL_TONE : VISIT_STATE_TONES[state]);

/**
 * A visit in the day list: everything about it at a glance. `subtle` is for
 * visits whose window is merely open that day (not their own day).
 */
export const VisitCard = ({ visit, date, projectName, projectSlot, onOpen, subtle, showNotes }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const info = describeVisit(t, visit, date, projectName);
  const tone = toneOf(visit, info.state);
  return (
    <li className="tw-block">
      <button
        type="button"
        onClick={() => onOpen(visit)}
        className={`tw-group tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-l-4 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
          subtle
            ? 'tw-border-dashed tw-border-slate-300 tw-bg-white/40 tw-p-2.5 hover:tw-bg-white/80 dark:tw-border-white/15 dark:tw-bg-white/[0.02] dark:hover:tw-bg-white/[0.06]'
            : 'tw-border-slate-200 tw-bg-white/80 tw-p-3 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:hover:tw-bg-white/[0.07]'
        } ${PROJECT_STRIPES[projectSlot]}`}
      >
        <span className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-xl tw-text-xs tw-font-bold tw-tabular-nums ${subtle ? 'tw-h-9 tw-w-9' : 'tw-h-11 tw-w-11'} ${tone.chip}`}>
          {info.state === 'completedDay' ? <LuCheck aria-hidden="true" className="tw-h-5 tw-w-5" strokeWidth={3} /> : (info.number || 'V')}
        </span>
        <span className="tw-min-w-0 tw-flex-1 tw-space-y-1">
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
            <span className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{info.patient}</span>
            {info.number && info.state === 'completedDay' && <span className="tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{info.number}</span>}
            {visit.screenFail && <Badge tone="danger">{label('screenFail')}</Badge>}
          </span>
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {projectName && <span className="tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{projectName}</span>}
            <span className="tw-inline">{info.dayLabel}</span>
            {info.type && <span className="tw-inline">{info.type}</span>}
            {info.time && <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums"><LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />{info.time}</span>}
          </span>
          <span className={`tw-block tw-text-xs tw-font-medium ${info.state === 'overdue' ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-slate-600 dark:tw-text-slate-300'}`}>{info.status}</span>
          {showNotes && visit.info && <span className="tw-block tw-whitespace-pre-line tw-break-words tw-rounded-lg tw-bg-slate-500/[0.06] tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-slate-600 dark:tw-bg-white/[0.04] dark:tw-text-slate-300">{visit.info}</span>}
          {info.pending.length > 0 && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-amber-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">
              <LuTriangleAlert aria-hidden="true" className="tw-h-3 tw-w-3" />
              {label('pendingDates', { items: info.pending.map((key) => t(`profilePage.calendar.pending.${key}`)).join(', ') })}
            </span>
          )}
        </span>
        <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-300 tw-transition-transform group-hover:tw-translate-x-0.5 group-hover:tw-text-teal-500 dark:tw-text-slate-600" />
      </button>
    </li>
  );
};

VisitCard.propTypes = {
  visit: PropTypes.object.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  projectName: PropTypes.string,
  projectSlot: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
  subtle: PropTypes.bool,
  // Vizit notu (info) kartta da gösterilir (Operasyon Yönetimi gün görünümü)
  showNotes: PropTypes.bool,
};

/**
 * A job in the day list. A job linked to a project shows the project's name and
 * its colour as a solid left stripe (the rest of the border stays dashed).
 */
export const JobCard = ({ job, date, projectName, projectSlot, onOpen, showNotes }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const state = jobState(job);
  const time = formatTimeLabel(getJobTime(job));
  return (
    <li className="tw-block">
      <button
        type="button"
        onClick={() => onOpen(job)}
        className={`tw-group tw-relative tw-flex tw-w-full tw-items-center tw-gap-3 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-white/60 tw-p-3 tw-text-left tw-transition-colors hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/15 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06] ${projectName ? 'tw-border-l-0 tw-pl-4' : ''}`}
      >
        {projectName && <span aria-hidden="true" className={`tw-absolute tw-inset-y-0 tw-left-0 tw-w-1 ${PROJECT_SWATCHES[projectSlot]}`} />}
        <span className={`tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-slate-500/10 ${JOB_STATE_TONES[state].text}`}>
          {job.status ? <LuCheck aria-hidden="true" className="tw-h-5 tw-w-5" strokeWidth={3} /> : <LuBriefcase aria-hidden="true" className="tw-h-5 tw-w-5" />}
        </span>
        <span className="tw-min-w-0 tw-flex-1 tw-space-y-1">
          <span className={`tw-block tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white ${job.status ? 'tw-line-through tw-decoration-1 tw-opacity-70' : ''}`}>{job.title}</span>
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {projectName && <span className="tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{projectName}</span>}
            <span className="tw-inline">{label('jobLabel')}</span>
            <span className="tw-inline">{label('jobDay', { n: jobDayNumber(job, date) })}</span>
            {time && <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums"><LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />{time}</span>}
          </span>
          {showNotes && job.description && <span className="tw-block tw-whitespace-pre-line tw-break-words tw-rounded-lg tw-bg-slate-500/[0.06] tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-slate-600 dark:tw-bg-white/[0.04] dark:tw-text-slate-300">{job.description}</span>}
        </span>
        <Badge tone={JOB_STATE_TONES[state].pill}>{label(`jobStatus.${job.status ? 'done' : 'ongoing'}`)}</Badge>
      </button>
    </li>
  );
};

JobCard.propTypes = {
  job: PropTypes.object.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  // Bağlı proje (yoksa boş)
  projectName: PropTypes.string,
  projectSlot: PropTypes.number,
  onOpen: PropTypes.func.isRequired,
  showNotes: PropTypes.bool,
};

/** Leave name(s) of a day, translated (falls back to the stored label). */
export const leaveText = (t, leaves) => {
  const labels = [...new Set(leaves.map((leave) => leaveTypeLabel(t, leave.typeKey, leave.typeKey ? leave.label : t('profilePage.calendar.onLeave'))))];
  return labels.join(', ');
};

/** "You are on leave" note at the top of a day. */
export const LeaveBanner = ({ leaves }) => {
  const { t } = useTranslation();
  if (leaves.length === 0) return null;
  return (
    <div className={`tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-px-3.5 tw-py-3 tw-text-sm ${LEAVE_TONE}`}>
      <LuTreePalm aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
      <div className="tw-min-w-0">
        <p className="tw-font-semibold">{t('profilePage.calendar.leaveBanner')}</p>
        <p className="tw-text-xs tw-opacity-80">
          {leaves.map((leave) => `${leaveText(t, [leave])}${leave.leaveDays ? ` · ${t('profilePage.calendar.leaveDays', { count: leave.leaveDays })}` : ''}`).join(' — ')}
        </p>
      </div>
    </div>
  );
};

LeaveBanner.propTypes = { leaves: PropTypes.arrayOf(PropTypes.object).isRequired };
